import React from "react";
import { useState, useEffect, useRef } from "react";
import { IconSvg, IconSvgSizes } from "@att-bit/duc.components.icon-svg";
import { KeyPress } from "@att-bit/duc.utils.key-press";
import {
  colorCobalt600,
  colorFuncBlue600,
  colorFuncRed600,
  colorGray700,
  colorGray800,
  colorWhite,
} from "@att-bit/duc.styles.tokens";

export type CalendarProps = {
  /** sets the default selected date in date picker */
  selectedDate?: Date | number;
  /** sets the due date in date picker */
  dueDate?: Date;
  /** set whether a range */
  isRange?: boolean;
  /** sets the start date in date picker in case of range selection */
  startDate?: Date | number;
  /** sets the end date in date picker in case of range selection */
  endDate?: Date | number;
  /** sets whether date picker input is start in case of range selection */
  isStartDate?: boolean;
  /** sets whether date picker input is end in case of range selection */
  isEndDate?: boolean;
  /** array of dates that will be disabled in the date picker */
  disabledDates?: Date[];
  /** set the active start date in date picker, everything before will be disabled */
  activeStartDate?: Date;
  /** set the active end date in date picker, everything after will be disabled */
  activeEndDate?: Date;
  /** set the alert message in calendar */
  alertMessage?: string;

  /** event triggered when a date is selected from primary date picker */
  onSelect?: (val: Date) => void;
  /** callback to parent date picker input to hide calendar */
  hideCalendar?: () => void;

  /** sets the label for date picker input */
  labelText?: string;
  /** sets the due date legend label */
  dueDateLabel?: string;
  /** sets the selected date legend label */
  selectedDateLabel?: string;
  /** sets the selected due date legend label */
  selectedDueDateLabel?: string;
  /** hides the next month icon when reaching the date provided in this props */
  disableNext?: Date;
  /** hides the prev month icon when reaching the date provided in this props */
  disablePrev?: Date;
  /** whether legend at bottom is shown */
  showLegendAtBottom?: boolean;
  /** set true if want to show calender only */
  showCalenderOnly?: boolean;
  /** sets whether calendar is shown */
  showCalendar?: boolean;

  /** This will be set as id for date picker input */
  testId?: string;
  /** used for unique identification of element */
  id: string;
};

// for memo: export const Calendar = React.memo(({
export const Calendar = ({
  activeStartDate = new Date(),
  activeEndDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  ),
  disablePrev, // = new Date(
  //  new Date(activeStartDate).setMonth(activeStartDate.getMonth() - 1)
  //),
  dueDateLabel = `Due date`,
  selectedDateLabel = `Selected payment date`,
  selectedDueDateLabel = `Selected date is the due date`,
  ...props
}: CalendarProps) => {
  // HOOKS ##############################################################

  /* useRef */
  const calendarContainerRef = useRef<HTMLElement>(null);
  const alertMsgRef = useRef<HTMLElement>(null);
  const tabElements = useRef<NodeListOf<Element>>(null);
  const dateTabElements = useRef<NodeListOf<Element>>(null);
  const firstEl = useRef<HTMLElement>(null);
  const lastEl = useRef<HTMLElement>(null);
  const el = useRef<HTMLElement>(null);
  const isNewPrevNextMonthLoaded = useRef(false);
  const indexOfCurrent = useRef<number>();
  const indexOfRow = useRef<number>();
  const monthIndexMap = useRef([
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ]);
  const daysDisplayLabel = useRef(["S", "M", "T", "W", "T", "F", "S"]);
  const daysLabel = useRef([
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ]);
  let rowGroup: any;
  const executeCallBack = useRef(false);
  const executeFirstFocusCallBack = useRef(false);
  const executeLastFocusCallBack = useRef(false);
  const executeUpArrowFocusCallBack = useRef(false);
  const executeDownArrowFocusCallBack = useRef(false);

  const getDaysInMonth = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    const days: Date[] = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const getDatesInRows = (dates: Date[]) => {
    const rows: any[] = [];
    let rowData: any[] = [];
    let count = 0;
    dates.map((date: Date, i: number) => {
      if (date.getDay() !== 0 && i === 0) {
        rowData.push("");
        while (rowData.length !== date.getDay()) {
          rowData.push("");
        }
        rowData.push(date);
      } else if (i + 1 === dates.length && rowData.length !== 7) {
        rowData.push(date);
        while (rowData.length !== 7) {
          rowData.push("");
        }
      } else {
        rowData.push(date);
      }

      if (rowData.length === 7) {
        rows[count] = [];
        rows[count] = rowData;
        rowData = [];
        count++;
      }
    });
    return rows;
  };
  const currDateRef = useRef(new Date());

  /* stateful items */
  const datesForMonth = getDaysInMonth(
    currDateRef.current.getMonth(),
    currDateRef.current.getFullYear()
  );

  const [currentMonthYear, setCurrentMonthYear] = useState(
    monthIndexMap.current[currDateRef.current.getMonth()] +
      " " +
      currDateRef.current.getFullYear()
  );
  const [datesToDisplay, setDatesToDisplay] = useState(
    getDatesInRows(datesForMonth)
  );
  const [showFocus, setShowFocus] = useState(false);
  const [disableNextMonth, setDisableNextMonth] = useState(false);
  const [disablePrevMonth, setDisablePrevMonth] = useState(false);

  /* asynchronous after render with cleanup - similar to componentDidMount, but not the same since layout is not available */
  useEffect(() => {
    if (
      !props.showCalenderOnly &&
      props.showCalendar &&
      calendarContainerRef.current
    ) {
      calendarContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    document.addEventListener("mousedown", setFocusFalse, false);
    document.addEventListener("keydown", setFocusTrue, false);

    collectCalenderData();
    const htmlTag = document.getElementsByTagName("html")[0];
    if (
      htmlTag &&
      htmlTag.classList &&
      htmlTag.classList.contains("is-tabbing") &&
      tabElements.current.length > 0
    ) {
      setFocusTrue();
      (tabElements.current[0] as HTMLElement).focus();
    }
    if (props.alertMessage && props.showCalendar && alertMsgRef.current) {
      alertMsgRef.current.focus();
    }

    if (props.showCalendar) {
      const date =
        typeof props.selectedDate === "object"
          ? new Date(props.selectedDate)
          : props.isRange
          ? props.isStartDate && typeof props.endDate === "object"
            ? new Date(props.endDate)
            : props.isEndDate && typeof props.startDate === "object"
            ? new Date(props.startDate)
            : new Date()
          : new Date();

      currDateRef.current = date;
      showNextPrevMonth(0);
    }

    return () => {
      // clean up - ex removeEventListeners...
      document.removeEventListener("mousedown", setFocusFalse, false);
      document.removeEventListener("keydown", setFocusTrue, false);
    };
  }, [props.showCalenderOnly, props.showCalendar, props.alertMessage]);

  /* asynchronous after render - only execute on state change for... */
  useEffect(() => {
    if (isNewPrevNextMonthLoaded.current) {
      collectCalenderData();
      isNewPrevNextMonthLoaded.current = false;
    }

    if (executeUpArrowFocusCallBack.current) {
      checkFocus(el.current, indexOfCurrent.current, 5);
    } else if (executeDownArrowFocusCallBack.current) {
      checkFocus(el.current, indexOfCurrent.current, 0);
    } else if (executeCallBack.current) {
      checkFocus(el.current, indexOfCurrent.current, indexOfRow.current);
    } else if (
      executeFirstFocusCallBack.current ||
      executeLastFocusCallBack.current
    ) {
      focusFirstorLastAvailableDate(el.current);
    }
  }, [
    executeUpArrowFocusCallBack.current,
    executeDownArrowFocusCallBack.current,
    executeCallBack.current,
    executeFirstFocusCallBack.current,
    executeLastFocusCallBack.current,
    isNewPrevNextMonthLoaded.current,
  ]);

  // METHODS #############################################################

  const collectCalenderData = () => {
    if (calendarContainerRef.current) {
      tabElements.current = calendarContainerRef.current.querySelectorAll(
        'th[tabindex="0"], td[tabindex="0"], div[tabindex="0"]'
      );
      dateTabElements.current = calendarContainerRef.current.querySelectorAll(
        'td[tabindex="0"]'
      );
      firstEl.current = tabElements.current[0] as HTMLElement;
      lastEl.current = tabElements.current[
        tabElements.current.length - 1
      ] as HTMLElement;
    }
  };

  const setFocus = (value: boolean) => {
    if (value !== showFocus) {
      setShowFocus(value);
    }
  };

  const setFocusFalse = () => {
    setFocus(false);
  };

  const setFocusTrue = () => {
    setFocus(true);
  };

  const handlePrevMonthKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (KeyPress.enterIsPressed(e)) {
      showPrevMonth();
    } else if (KeyPress.escIsPressed(e)) {
      if (props.hideCalendar) props.hideCalendar();
    } else if (
      KeyPress.downArrowIsPressed(e) ||
      KeyPress.rightArrowIsPressed(e)
    ) {
      e.preventDefault();
      const tBody: Element = e.currentTarget.parentElement?.nextElementSibling;
      if (tBody.querySelector(`td.color-cobalt-600.selected`)) {
        (tBody.querySelector(
          `td.color-cobalt-600.selected`
        ) as HTMLElement).focus();
      } else if (tBody.querySelector(`td.color-cobalt-600`)) {
        (tBody.querySelector(`td.color-cobalt-600`) as HTMLElement).focus();
      }
    } else if (e.shiftKey && KeyPress.tabIsPressed(e)) {
      (tabElements.current[0] as HTMLElement).focus();
    }
  };

  const handleNextMonthKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (KeyPress.enterIsPressed(e)) {
      showNextMonth();
    } else if (e.shiftKey && KeyPress.tabIsPressed(e)) {
      (tabElements.current[1] as HTMLElement).focus();
    } else if (
      KeyPress.tabIsPressed(e) ||
      KeyPress.downArrowIsPressed(e) ||
      KeyPress.rightArrowIsPressed(e)
    ) {
      e.preventDefault();
      const tBody: Element = e.currentTarget.parentElement?.nextElementSibling;
      if (tBody.querySelector(`td.color-cobalt-600.selected`)) {
        (tBody.querySelector(
          `td.color-cobalt-600.selected`
        ) as HTMLElement).focus();
      } else if (tBody.querySelector(`td.color-cobalt-600`)) {
        (tBody.querySelector(`td.color-cobalt-600`) as HTMLElement).focus();
      } else {
        (tabElements.current[0] as HTMLElement).focus();
      }
    } else if (KeyPress.escIsPressed(e)) {
      if (props.hideCalendar) props.hideCalendar();
    }
  };

  const handleDateKeyDown = (
    date: Date | string,
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    executeCallBack.current = false;
    executeUpArrowFocusCallBack.current = false;
    executeDownArrowFocusCallBack.current = false;
    if (e.currentTarget.parentElement) {
      el.current = e.currentTarget.parentElement;
    }
    const tdElement: Element = el.current.querySelector(".date:focus");
    const datesSiblings = el.current?.querySelectorAll("td");
    indexOfCurrent.current = parseInt(tdElement.getAttribute("data-key"), 10);
    const dataKey = el.current.getAttribute("data-key");
    if (dataKey) {
      indexOfRow.current = parseInt(dataKey, 10);
    }

    e.preventDefault();
    if (e.shiftKey && KeyPress.pageUpIsPressed(e)) {
      if (!disableNextMonth) {
        showNextPrevMonth(null, 1);
        executeCallBack.current = true;
      }
    } else if (e.shiftKey && KeyPress.pageDownIsPressed(e)) {
      if (!disablePrevMonth) {
        showNextPrevMonth(null, -1);
        executeCallBack.current = true;
      }
    } else if (e.shiftKey && KeyPress.tabIsPressed(e)) {
      (tabElements.current[1] as HTMLElement).focus();
    } else if (KeyPress.enterIsPressed(e)) {
      if (typeof date === "object") {
        selectDate(date);
      } else {
        return;
      }
    } else if (KeyPress.downArrowIsPressed(e)) {
      if (indexOfCurrent.current !== -1) {
        if (el.current?.nextSibling) {
          const nextVertTrSibling = el.current?.nextSibling.childNodes;
          if (
            (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)
              .innerHTML !== "" &&
            (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)
              .tabIndex !== -1
          ) {
            // && (nextVertTrSibling[indexOfCurrent] as HTMLElement).tabIndex === 0
            (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)?.focus();
          } else {
            if (
              !disableNextMonth &&
              (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)
                .innerHTML === ""
            ) {
              showNextPrevMonth(1);
              executeDownArrowFocusCallBack.current = true;
            }
          }
        } else {
          if (!disableNextMonth) {
            showNextPrevMonth(1);
            executeDownArrowFocusCallBack.current = true;
          }
        }
      }
    } else if (KeyPress.upArrowIsPressed(e)) {
      if (el.current?.previousSibling) {
        const nextVertTrSibling = el.current?.previousSibling.childNodes;
        if (
          (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)
            .innerHTML !== "" &&
          (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)
            .tabIndex !== -1
        ) {
          (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)?.focus();
        } else {
          if (
            !disablePrevMonth &&
            (nextVertTrSibling[indexOfCurrent.current] as HTMLElement)
              .innerHTML === ""
          ) {
            showNextPrevMonth(-1);
            executeUpArrowFocusCallBack.current = true;
          }
        }
      } else {
        if (!disablePrevMonth) {
          showNextPrevMonth(-1);
          executeUpArrowFocusCallBack.current = true;
        }
      }
    } else if (KeyPress.tabIsPressed(e)) {
      firstEl.current?.focus();
    } else if (KeyPress.rightArrowIsPressed(e)) {
      if (tdElement.nextElementSibling) {
        if (
          tdElement.nextElementSibling.innerHTML !== "" &&
          (tdElement.nextElementSibling as HTMLElement).tabIndex !== -1
        ) {
          (tdElement.nextElementSibling as HTMLElement).focus();
        } else {
          if (
            !disableNextMonth &&
            tdElement.nextElementSibling.innerHTML === ""
          ) {
            showNextPrevMonth(1);
            executeFirstFocusCallBack.current = true;
          }
        }
      } else if (el.current.nextSibling) {
        if (
          (el.current.nextSibling.childNodes[0] as HTMLElement).innerHTML !==
            "" &&
          (el.current.nextSibling.childNodes[0] as HTMLElement).tabIndex !== -1
        ) {
          (el.current.nextSibling.childNodes[0] as HTMLElement)?.focus();
        }
      } else {
        if (!disableNextMonth) {
          showNextPrevMonth(1);
          executeFirstFocusCallBack.current = true;
        }
      }
    } else if (KeyPress.leftArrowIsPressed(e)) {
      if (tdElement.previousElementSibling) {
        if (
          tdElement.previousElementSibling.innerHTML !== "" &&
          (tdElement.previousElementSibling as HTMLElement).tabIndex !== -1
        ) {
          (tdElement.previousElementSibling as HTMLElement).focus();
        } else {
          if (
            !disablePrevMonth &&
            tdElement.previousElementSibling.innerHTML === ""
          ) {
            showNextPrevMonth(-1);
            executeLastFocusCallBack.current = true;
          }
        }
      } else if (el.current.previousSibling) {
        if (
          (el.current.previousSibling.childNodes[6] as HTMLElement).tabIndex !==
            -1 &&
          (el.current.previousSibling.childNodes[6] as HTMLElement)
            .innerHTML !== ""
        ) {
          (el.current.previousSibling.childNodes[6] as HTMLElement)?.focus();
        }
      } else {
        if (!disablePrevMonth) {
          showNextPrevMonth(-1);
          executeLastFocusCallBack.current = true;
        }
      }
    } else if (KeyPress.homeIsPressed(e)) {
      datesSiblings[0]?.focus();
    } else if (KeyPress.endIsPressed(e)) {
      datesSiblings[6]?.focus();
    } else if (KeyPress.pageUpIsPressed(e)) {
      if (!disableNextMonth) {
        showNextPrevMonth(1);
        executeCallBack.current = true;
      }
    } else if (KeyPress.pageDownIsPressed(e)) {
      if (!disablePrevMonth) {
        showNextPrevMonth(-1);
        executeCallBack.current = true;
      }
    } else if (KeyPress.escIsPressed(e)) {
      if (props.hideCalendar) {
        props.hideCalendar();
      }
    }
  };

  const checkFocus = (
    el: HTMLElement,
    indexOfCurrent: number,
    indexOfRow: number
  ) => {
    const tBody: HTMLElement = el.parentElement;
    const currFocus = document.getElementsByTagName("td:focus");
    const currRow = tBody.querySelector(`tr[data-key="${indexOfRow}"]`);
    const prevRow = tBody.querySelector(`tr[data-key="${indexOfRow - 1}"]`);
    const nextRow = tBody.querySelector(`tr[data-key="${indexOfRow + 1}"]`);

    if (currFocus.length === 0) {
      focusAvailableTd(currRow, prevRow, nextRow, indexOfCurrent);
    }
  };

  const focusAvailableTd = (
    currRow: Element,
    prevRow: Element,
    nextRow: Element,
    indexOfCurrent: number
  ) => {
    const currEle: HTMLElement =
      currRow &&
      currRow.querySelector(
        'td[data-key="' + indexOfCurrent + '"].color-cobalt-600'
      )
        ? currRow.querySelector(
            'td[data-key="' + indexOfCurrent + '"].color-cobalt-600'
          )
        : undefined;
    const prevEle: HTMLElement =
      prevRow &&
      prevRow.querySelector(
        'td[data-key="' + indexOfCurrent + '"].color-cobalt-600'
      )
        ? prevRow.querySelector(
            'td[data-key="' + indexOfCurrent + '"].color-cobalt-600'
          )
        : undefined;
    const nextEle: HTMLElement =
      nextRow &&
      nextRow.querySelector(
        'td[data-key="' + indexOfCurrent + '"].color-cobalt-600'
      )
        ? nextRow.querySelector(
            'td[data-key="' + indexOfCurrent + '"].color-cobalt-600'
          )
        : undefined;

    if (currEle && currEle.getElementsByTagName("div").length > 0) {
      currEle.focus();
    } else if (prevEle && prevEle.getElementsByTagName("div").length > 0) {
      prevEle.focus();
    } else if (nextEle && nextEle.getElementsByTagName("div").length > 0) {
      nextEle.focus();
    } else {
      (dateTabElements.current[0] as HTMLElement).focus();
    }
  };

  const focusFirstorLastAvailableDate = (el: HTMLElement) => {
    const tBody = el.parentElement;
    let availableDates;
    if (tBody) {
      availableDates = tBody.querySelectorAll('td[tabindex="0"]');
      if (executeFirstFocusCallBack.current) {
        executeFirstFocusCallBack.current = false;
        availableDates[0].focus();
      } else if (executeLastFocusCallBack.current) {
        executeLastFocusCallBack.current = false;
        if (availableDates[availableDates.length - 1]) {
          availableDates[availableDates.length - 1].focus();
        }
      }
    }
  };

  const showNextPrevMonth = (month?: number, year?: number) => {
    let disableNextMonth = false;
    let disablePrevMonth = false;
    const date = currDateRef.current;
    month && month === 1
      ? date.setMonth(date.getMonth() + 1, 1)
      : month && month === -1
      ? date.setMonth(date.getMonth() - 1, 1)
      : date.setMonth(date.getMonth());
    year && year === 1
      ? date.setFullYear(date.getFullYear() + 1)
      : year && year === -1
      ? date.setFullYear(date.getFullYear() - 1)
      : date.setFullYear(date.getFullYear());
    if ((month && month === 1) || month === 0) {
      const nDate = new Date(date);
      nDate.setMonth(nDate.getMonth() + 1, 1);

      disableNextMonth =
        props.disableNext?.getFullYear() === nDate.getFullYear() &&
        props.disableNext?.getMonth() === nDate.getMonth()
          ? true
          : false;
    }
    if ((month && month === -1) || month === 0) {
      const pDate = new Date(date);
      pDate.setMonth(pDate.getMonth() - 1, 1);

      disablePrevMonth =
        disablePrev?.getFullYear() === pDate.getFullYear() &&
        disablePrev?.getMonth() === pDate.getMonth()
          ? true
          : false;
    }
    const daysInMonth = getDaysInMonth(date.getMonth(), date.getFullYear());
    const datesInRows = getDatesInRows(daysInMonth);
    setDatesToDisplay(datesInRows);
    setCurrentMonthYear(
      monthIndexMap.current[date.getMonth()] + " " + date.getFullYear()
    );
    currDateRef.current = date;
    setDisableNextMonth(disableNextMonth);
    setDisablePrevMonth(disablePrevMonth);

    isNewPrevNextMonthLoaded.current = true;
  };

  const showNextMonth = () => {
    showNextPrevMonth(1);
  };

  const showPrevMonth = () => {
    showNextPrevMonth(-1);
  };

  const selectDate = (date: Date) => {
    if (props.onSelect) {
      props.onSelect(date);
    }
  };

  const noSelection = () => {
    return;
  };

  const iconShow =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="img" focusable="false"><path class="svg-base" d="M16 1a15 15 0 1015 15A15 15 0 0016 1zm1 22h-2v-2h2zm0-4h-2V9h2z" /></svg>';

  // RENDER ##############################################################

  return (
    <>
      <div
        id={`calendar-wrapper-${props.id}`}
        aria-hidden={props.showCalendar ? "false" : "true"}
        className={`calendar-wrapper flex opacity0 rel ${
          props.showCalenderOnly
            ? `fadein`
            : props.showCalendar
            ? `fadein z1`
            : `fadeout`
        } ${!props.showCalendar ? `overflow-hidden` : ``}`}
      >
        <div
          id={`calendar-${props.id}`}
          role="dialog"
          aria-label={props.labelText}
          ref={(ref) => {
            calendarContainerRef.current = ref;
          }}
          className={`calendar-drop-down absolute top4 bg-white radius-md border border-gray-400 z2 ${
            !props.showCalenderOnly ? `box-shadow1` : ``
          } ${!props.showCalendar ? `hide` : ``} ${
            !props.isRange ? `width-full` : ``
          } ${props.isEndDate ? `right` : props.isStartDate ? `left` : ``}`}
        >
          <div className={"datepicker-days pad-t-xxs pad-b-xxs"}>
            {props.alertMessage ? (
              <div className="flex flex-items-stretch mar-t-xxs mar-r-xs mar-r-sm-sm mar-l-xs mar-l-sm-sm mar-b-xxs rel">
                <div className="flex height-sm-all width-sm-all flex-shrink-0 mar-r-xxs">
                  <IconSvg
                    size={IconSvgSizes.SM}
                    svgNodeStr={iconShow}
                    aria-hidden={true}
                    color="color-red-600"
                  />
                </div>
                <p
                  id={`alert-msg-${props.id}`}
                  className="type-sm mar-t-1-sm"
                  ref={(ref) => (alertMsgRef.current = ref)}
                  tabIndex={-1}
                >
                  {props.alertMessage}
                </p>
              </div>
            ) : (
              ""
            )}
            <div className="flex flex-items-center justify-between">
              <div
                id={`prev-month-${props.id}`}
                style={{
                  visibility: disablePrevMonth ? `hidden` : `visible`,
                }}
                aria-hidden={disablePrevMonth}
                className="flex flex-centered touch-space overflow-hidden"
                onKeyDown={handlePrevMonthKeyDown}
                onClick={showPrevMonth}
                tabIndex={!disablePrevMonth ? 0 : -1}
                role="button"
                aria-label="Previous month"
              >
                <i className="flex height-sm-all width-sm-all icon-chevron-blue rotate180"></i>
              </div>
              <div
                role="presentation"
                id={`month-${props.id}`}
                className={`font-bold color-gray-700 type-22`}
                aria-live="polite"
              >
                {currentMonthYear}
              </div>
              <div
                id={`next-${props.id}`}
                style={{
                  visibility: disableNextMonth ? "hidden" : "visible",
                }}
                aria-hidden={disableNextMonth}
                className="flex flex-centered touch-space overflow-hidden"
                onKeyDown={handleNextMonthKeyDown}
                onClick={showNextMonth}
                tabIndex={!disableNextMonth ? 0 : -1}
                role="button"
                aria-label="Next month"
              >
                <i className="flex height-sm-all width-sm-all icon-chevron-blue"></i>
              </div>
            </div>
            <table
              role="grid"
              aria-labelledby={`month-${props.id}`}
              className={"width-full"}
            >
              <thead>
                <tr
                  role="row"
                  aria-hidden="true"
                  className={
                    "flex justify-between mar-t-xxs mar-b-xxxs type-16"
                  }
                >
                  {daysDisplayLabel.current.map(
                    (dayLabel: string, i: number) => {
                      return (
                        <th
                          key={i}
                          id={daysLabel.current[i]}
                          className={`block font-bold color-gray-700 height-md-all width-md-all`}
                        >
                          {dayLabel}
                        </th>
                      );
                    }
                  )}
                </tr>
                <tr aria-hidden="true">
                  <th colSpan={7} className="pad-l-xxs pad-r-xxs pad-b-xxs">
                    <div
                      className="border-bottom border-gray-300"
                      style={{ height: "1px" }}
                    ></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {datesToDisplay.map((dates: any, i: number) => {
                  let checkEndDateShowDate = -1;
                  rowGroup = [];
                  if (
                    props.isRange &&
                    props.isEndDate &&
                    typeof props.selectedDate !== "object"
                  ) {
                    if (typeof props.startDate === "object") {
                      checkEndDateShowDate = props.startDate.setHours(
                        0,
                        0,
                        0,
                        0
                      );
                    }
                  }
                  dates.map((date: Date, j: number) => {
                    if (typeof date === "object") {
                      const sd =
                        typeof props.selectedDate === "object"
                          ? props.selectedDate.setHours(0, 0, 0, 0)
                          : -1;
                      const dueDate =
                        typeof props.dueDate === "object"
                          ? props.dueDate.setHours(0, 0, 0, 0)
                          : -1;
                      const regularDate = date.setHours(0, 0, 0, 0);
                      let withInRange: boolean = false;
                      let firstRangeEle: boolean = false;
                      let lastRangeEle: boolean = false;
                      const isDisabledDate: Date[] = props.disabledDates
                        ? props.disabledDates.filter(
                            (item) => item.setHours(0, 0, 0, 0) === regularDate
                          )
                        : [];
                      const isPreviousDate: boolean =
                        regularDate < activeStartDate.setHours(0, 0, 0, 0) ||
                        (props.isEndDate && regularDate < props.startDate);
                      const isNextDate: boolean =
                        regularDate > activeEndDate.setHours(0, 0, 0, 0);
                      if (
                        typeof props.startDate === "object" &&
                        typeof props.endDate === "object"
                      ) {
                        if (date >= props.startDate && date <= props.endDate) {
                          withInRange = true;
                          if (
                            regularDate === props.startDate.setHours(0, 0, 0, 0)
                          ) {
                            firstRangeEle = true;
                          }
                          if (
                            regularDate === props.endDate.setHours(0, 0, 0, 0)
                          ) {
                            lastRangeEle = true;
                          }
                        }
                      }
                      rowGroup.push(
                        <td
                          className={`date rel flex-centered flex type-16 font-bold ${
                            isDisabledDate.length === 1 ||
                            isPreviousDate ||
                            isNextDate
                              ? `color-gray-500 disabled-date`
                              : `color-cobalt-600`
                          } ${withInRange ? `in-range color-ui-white` : ``} ${
                            firstRangeEle ? `first-range-ele` : ``
                          } ${lastRangeEle ? `last-range-ele` : ``} ${
                            (!withInRange && sd === regularDate) ||
                            (checkEndDateShowDate !== -1 &&
                              checkEndDateShowDate === regularDate)
                              ? `selected`
                              : ``
                          } ${dueDate === regularDate ? `due-date` : ``}`}
                          role="gridcell"
                          tabIndex={
                            isDisabledDate.length === 1 ||
                            isPreviousDate ||
                            isNextDate
                              ? -1
                              : 0
                          }
                          data-key={j}
                          key={j}
                          onClick={
                            isDisabledDate.length === 1 ||
                            isPreviousDate ||
                            isNextDate
                              ? noSelection
                              : () => selectDate(date)
                          }
                          onKeyDown={
                            isDisabledDate.length === 1 ||
                            isPreviousDate ||
                            isNextDate
                              ? (e) => handleDateKeyDown("disabled", e)
                              : (e) => handleDateKeyDown(date, e)
                          }
                          aria-selected={sd === regularDate}
                          aria-disabled={
                            isDisabledDate.length === 1 ||
                            isPreviousDate ||
                            isNextDate
                          }
                        >
                          <span
                            className={"flex flex-centered"}
                            aria-hidden={true}
                          >
                            {date.getDate()}
                          </span>
                          <div className="hidden-spoken">{`${
                            daysLabel.current[j]
                          } ${date.getDate()} ${
                            dueDate === regularDate ? `- current due date` : ``
                          }`}</div>
                        </td>
                      );
                    } else {
                      rowGroup.push(
                        <td
                          key={j}
                          role="gridcell"
                          className={`date rel flex-centered flex type-base font-bold`}
                        />
                      );
                    }
                  });
                  return (
                    <tr
                      role="row"
                      className={"flex justify-between"}
                      key={i}
                      data-key={i}
                    >
                      {rowGroup.map((td: any, key: number) => {
                        return td;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {props.showLegendAtBottom && (
              <div className={`calendar-legend`} aria-hidden="true">
                <ul>
                  <li className={`type-16 mar-b-xxxs color-gray-700`}>
                    <i className={`due-date-legend flex-shrink-0`} />
                    {dueDateLabel}
                  </li>
                  <li className={`type-16 mar-b-xxxs color-gray-700`}>
                    <i className={`selected-date-legend flex-shrink-0`} />
                    {selectedDateLabel}
                  </li>
                  <li className={`type-16 mar-b-none color-gray-700`}>
                    <i className={`selected-due-date-legend flex-shrink-0`} />
                    {selectedDueDateLabel}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        :global(.is-tabbing .calendar-drop-down th:focus),
        :global(.is-tabbing .calendar-drop-down td:focus) {
          outline: 1px dotted ${colorGray800.value};
          box-shadow: 0 0 0 1px ${colorWhite.value} inset;
        }
        :global(.is-tabbing .calendar-drop-down th:focus) {
          outline-offset: -2px;
        }

        .calendar-drop-down {
          min-width: 380px;
          max-width: 380px;
        }

        @media (max-width: 428px) {
          .calendar-drop-down {
            min-width: 310px;
          }
        }

        .calendar-drop-down .datepicker-days thead th {
          width: 44px;
        }

        .calendar-drop-down .datepicker-days tbody td {
          height: 44px;
          width: 44px;
        }

        .calendar-drop-down .date {
          cursor: pointer;
        }

        .calendar-drop-down .date:hover {
          text-decoration: underline;
        }
        .calendar-drop-down .date.disabled-date:hover,
        .calendar-drop-down .date.disabled-date span:hover {
          text-decoration: none;
          cursor: not-allowed;
        }

        .calendar-drop-down .date.in-range span {
          background-color: ${colorCobalt600.value};
          color: ${colorWhite.value};
          width: 32px;
          height: 32px;
        }

        .calendar-drop-down .date.in-range span:before {
          background-color: ${colorCobalt600.value};
          color: ${colorWhite.value};
          content: "";
          position: absolute;
          width: 32px;
          height: 32px;
          left: 32px;
          top: 6px;
          z-index: -1;
        }
        .calendar-drop-down .date.in-range:nth-child(7) span:before,
        .calendar-drop-down .date.last-range-ele span:before {
          left: 6px;
        }

        .calendar-drop-down .date.first-range-ele span,
        .calendar-drop-down .date.first-range-ele span:before {
          border-top-left-radius: 0.3rem;
          border-bottom-left-radius: 0.3rem;
          overflow: hidden;
        }

        .calendar-drop-down .date.last-range-ele span,
        .calendar-drop-down .date.last-range-ele span:before {
          border-top-right-radius: 0.3rem;
          border-bottom-right-radius: 0.3rem;
          overflow: hidden;
        }

        :global(.is-tabbing .calendar-drop-down .date:focus) {
          outline-offset: -1px;
        }

        .calendar-drop-down .date.due-date span {
          background-color: ${colorFuncRed600.value};
          border-radius: 2em;
          color: ${colorWhite.value};

          height: 32px;

          width: 32px;
        }

        .calendar-drop-down .date.selected span {
          background-color: ${colorFuncBlue600.value};
          border-radius: 0.3rem;
          color: ${colorWhite.value};
          height: 32px;
          width: 32px;
        }
        .calendar-drop-down .date.in-range:hover,
        .calendar-drop-down .date.selected:hover,
        .calendar-drop-down .date.due-date:hover {
          text-decoration: none;
        }

        .calendar-drop-down .date.due-date.selected span {
          background-color: ${colorFuncRed600.value};
          border-radius: 2em;
          color: ${colorWhite.value};
          width: 24px;
          height: 24px;
        }

        .calendar-drop-down .date.due-date.selected span:before {
          background-color: transparent;
          border: 2px solid ${colorCobalt600.value};
          content: "";
          height: 32px;
          width: 32px;
          border-radius: 0.3rem;
          position: absolute;
        }

        .calendar-drop-down .calendar-legend {
          padding: 0 12px;
        }

        .calendar-drop-down .calendar-legend li {
          align-items: baseline;
          display: flex;
          color: ${colorGray700.value};
          margin-bottom: 4px;
          min-height: 2.6rem;
          text-align: left;
        }

        .calendar-legend .due-date-legend,
        .calendar-legend .selected-date-legend,
        .calendar-legend .selected-due-date-legend {
          display: inline-flex;
          height: 16px;
          width: 16px;
          margin-right: 8px;
          position: relative;
          top: 2px;
        }

        .calendar-legend .due-date-legend {
          background-color: ${colorFuncRed600.value};
          border-radius: 50%;
        }

        .calendar-legend .selected-date-legend {
          background-color: ${colorCobalt600.value};
          border-radius: 0.3rem;
        }

        .calendar-legend .selected-due-date-legend {
          border: 2px solid ${colorFuncBlue600.value};
          border-radius: 0.3rem;
        }

        .calendar-legend .selected-due-date-legend:after {
          background-color: ${colorFuncRed600.value};
          border-radius: 50%;
          content: "";
          display: block;
          height: 10px;
          left: 1px;
          position: absolute;
          top: 1px;
          width: 10px;
        }
      `}</style>
    </>
  );
};
